/**
 * Parts fit chip (device check phase 2, 11/2/2026): one look at whether a
 * part fits the device. Green ✓ fits, red ✗ made for something else, grey ?
 * when nobody has said what it fits yet. Words come from the server's one
 * partFitsDevice — the chip never decides anything itself.
 */
import { cn } from "@/lib/utils";

export type FitVerdict = "fits" | "fits_any" | "wrong_model" | "wrong_size" | "wrong_type" | "wrong_make" | "unknown";

export interface PartFitResult {
    verdict: FitVerdict;
    reason: string;
    label: string;
    models: string[];
    source: "office" | "catalog" | "name" | null;
}

export function isWrongFit(v: FitVerdict | undefined | null): boolean {
    return v === "wrong_model" || v === "wrong_size" || v === "wrong_type" || v === "wrong_make";
}

export function FitChip({ fit, className }: { fit: PartFitResult | null | undefined; className?: string }) {
    if (!fit) return null;
    const tone =
        fit.verdict === "fits" || fit.verdict === "fits_any"
            ? "bg-emerald-600 text-white"
            : isWrongFit(fit.verdict)
              ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
              : "bg-muted text-foreground/70";
    return (
        <span
            className={cn(
                "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-bold",
                tone,
                className,
            )}
            title={fit.reason}
            data-testid="fit-chip"
            data-verdict={fit.verdict}
        >
            {fit.label}
        </span>
    );
}
